import { Trans } from '@lingui/macro';
import React, { FC, ReactElement, ReactNode, useState } from 'react';
import Slider from 'react-slick';

import Feedback from 'Components/Feedback/Feedback';
import FeedbackCard from 'Components/FeedbackCard/FeedbackCard';
import { TFeedback } from 'Components/FeedbackCard/FeedbackCard.helpers';
import 'common/styles/SlickSlider.scss';

import './Feedbacks.scss';

type Props = {
  feedbacks?: TFeedback[];
  internalFeedbacks?: TFeedback[];
  externalFeedbacks?: TFeedback[];
  type: 'block' | 'slider';
  showRatedLabel?: boolean;
  isExpandable?: boolean;
  allowReply?: boolean;
  isSameUser?: boolean;
  preferredCategoryName?: string;
  showProvider?: boolean;
  /**
   * enables rendering to html all feedbacks for SEO purposes
   */
  allowVisuallyHidden?: boolean;
  disableProfileLinks?: boolean;
  l4Name?: string;
  provider?: {
    name: string;
    avatar: string;
    city: string;
  };
  /**
   * SEO: use L4+City city name instead of real name.
   * see https://naspersclassifieds.atlassian.net/browse/GS-7744
   */
  city?: string;
  sendFeedbackReply?: (feedbackId: string, comment: string) => Promise<any>;
};

const sliderSettings = {
  infinite: true,
  speed: 500,
  slidesToShow: 3,
  slidesToScroll: 1,
  responsive: [
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 2,
      },
    },
    {
      breakpoint: 568,
      settings: {
        slidesToShow: 1,
      },
    },
  ],
};

const MAX_VISIBLE_FEEDBACKS = 2;
const MIN_ITEMS_TO_SLIDE = 4;

const Feedbacks: FC<Props> = (props) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const parseFeedback = (feedback: any) => {
    if (!feedback.user) {
      feedback = {
        ...feedback,
        user: {
          name: feedback.authorFirstName,
          avatar: feedback.authorAvatarUrl,
        },
      };
    }

    if (!feedback.provider && props.provider) {
      feedback = { ...feedback, provider: props.provider };
    }

    if (props.disableProfileLinks && feedback.provider.slug)
      feedback = { ...feedback, provider: { ...feedback.provider, slug: undefined } };

    if (!feedback.rating) {
      feedback = { ...feedback, rating: feedback.value };
    }

    if (props.city) {
      feedback = {
        ...feedback,
        user: { ...feedback.user, city: props.city },
        provider: { ...feedback.provider, city: props.city },
      };
    }

    return feedback;
  };

  const getFeedbacksCount = (): number => {
    const { feedbacks, internalFeedbacks, externalFeedbacks } = props;
    if (feedbacks) return feedbacks.length;
    if (internalFeedbacks && externalFeedbacks) {
      return [...internalFeedbacks, ...externalFeedbacks].length;
    }
    return 0;
  };

  const feedbacksCount = getFeedbacksCount();

  const getFeedbacksToDisplay = (feedbacks: TFeedback[]): TFeedback[] => {
    return props.isExpandable && !isExpanded ? feedbacks.slice(0, MAX_VISIBLE_FEEDBACKS) : feedbacks;
  };

  const renderFeedback = (feedback: TFeedback): ReactElement => {
    const Wrapper = props.type === 'block' ? Feedback : FeedbackCard;

    return (
      <Wrapper
        {...parseFeedback(feedback)}
        type={props.type}
        showRatedLabel={props.showRatedLabel}
        allowReply={props.allowReply}
        isSameUser={props.isSameUser}
        preferredCategoryName={props.preferredCategoryName}
        sendFeedbackReply={props.sendFeedbackReply}
        showProvider={props.showProvider}
        key={`feedback_${feedback.id}`}
        providerCategoryName={props.l4Name}
      />
    );
  };

  const renderFeedbacksList = (): ReactNode => {
    const { feedbacks } = props;
    if (feedbacks) {
      const visibleFeedbacks = getFeedbacksToDisplay(feedbacks);
      const visuallyHiddenFeedbacks = feedbacks.slice(visibleFeedbacks.length);

      return props.allowVisuallyHidden
        ? [...visibleFeedbacks.map(renderFeedback), renderVisuallyHiddenList(visuallyHiddenFeedbacks)]
        : visibleFeedbacks.map(renderFeedback);
    }
  };

  const renderVisuallyHiddenList = (items: TFeedback[]): ReactNode => {
    return items.map((feedback) => (
      <div className="visually-hidden" key={`feedback_${feedback.id}`}>
        {renderFeedback(feedback)}
      </div>
    ));
  };

  const showExpandButton: boolean =
    Boolean(props.isExpandable) && feedbacksCount > MAX_VISIBLE_FEEDBACKS && !isExpanded;

  return (
    <div className="feedbacks">
      {props.type === 'slider' && feedbacksCount >= MIN_ITEMS_TO_SLIDE ? (
        <Slider {...sliderSettings}>{renderFeedbacksList()}</Slider>
      ) : (
        renderFeedbacksList()
      )}
      {showExpandButton && (
        <button className="feedbacks__expand" type="button" onClick={() => setIsExpanded(true)}>
          <Trans>Pokaż więcej opinii</Trans>
        </button>
      )}
    </div>
  );
};

export default Feedbacks;
